/**
 * MagicLinkCallback.jsx
 * 
 * Handles magic link verification when customer clicks email link.
 * Verifies the token, creates session, and redirects to intended page.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MagicLinkCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshAuth } = useCustomerAuth();
  
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyMagicLink = async () => {
      const token = searchParams.get('token');
      const redirect = searchParams.get('redirect') || '/';

      if (!token) {
        setStatus('error');
        setError('No token provided');
        return;
      }

      try {
        const response = await fetch(
          `${API_URL}/api/customer/auth/magic-link/verify?token=${token}&redirect=${encodeURIComponent(redirect)}`,
          { 
            method: 'GET',
            credentials: 'include'
          }
        );

        if (response.ok) {
          const data = await response.json();
          setStatus('success');
          
          // Small delay to show success message, then redirect with page refresh
          setTimeout(() => {
            // Use window.location for a full page refresh to pick up cookies
            const targetUrl = data.redirect || '/';
            window.location.href = targetUrl;
          }, 1500);
        } else {
          const data = await response.json();
          setStatus('error');
          setError(data.detail || 'Failed to verify link');
        }
      } catch (err) {
        console.error('Magic link verification error:', err);
        setStatus('error');
        setError('Something went wrong. Please try again.');
      }
    };

    verifyMagicLink();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-soft p-8 text-center">
        {status === 'verifying' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Signing you in...
            </h1>
            <p className="text-gray-500">
              Please wait while we verify your link.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              You&apos;re signed in!
            </h1>
            <p className="text-gray-500">
              Redirecting you now...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Link expired or invalid
            </h1>
            <p className="text-gray-500 mb-6">
              {error}
            </p>
            <Button 
              onClick={() => navigate('/marketplace?signin=true')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Try signing in again
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default MagicLinkCallback;
